import {
    Icon,
    jnjArrowLeft,
    jnjArrowRight,
    jnjPause,
    jnjVideoPlay,
} from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { mdiChevronLeft, mdiChevronRight, mdiPlay, mdiPause } from "@mdi/js";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import classNames from "classnames";
import React, { ReactNode, useContext, useEffect, useState } from "react";

import { ButtonBack, ButtonNext, ButtonPlay, CarouselContext, DotGroup, Slide, Slider } from "pure-react-carousel";
import style from "./style.module.scss";

interface Props {
    children?: ReactNode | ReactNode[];
    interval: { automatic: boolean; speed: number };
    fullWidth?: boolean;
    roundedCorners?: boolean;
    pauseOnHover?: boolean;
    setPlaying?: React.Dispatch<React.SetStateAction<boolean>>;
    actionButtonClick?: (action: string, index: number) => void;
    carouselPreviousButtonAriaLabel?: string;
    carouselNextButtonAriaLabel?: string;
    carouselPlayButtonAriaLabel?: string;
    carouselPauseButtonAriaLabel?: string;
    carouselSliderAriaLabel?: string;
    carouselSlideAriaLabel?: string;
    carouselSlideDotAriaLabel?: string;
    headerVariant?: boolean;
}

export const CarouselContents = (props: Props): JSX.Element => {
    const {
        children,
        actionButtonClick,
        interval,
        fullWidth = false,
        roundedCorners = false,
        pauseOnHover = false,
        setPlaying = (value) => value,
        carouselPreviousButtonAriaLabel = "previous",
        carouselNextButtonAriaLabel = "next",
        carouselPlayButtonAriaLabel = "play",
        carouselPauseButtonAriaLabel = "pause",
        carouselSliderAriaLabel = "Relevant items",
        carouselSlideAriaLabel = "slide",
        carouselSlideDotAriaLabel = "slide dot",
        headerVariant = false,
    } = props;

    const { jnjFullBranded } = useJnjBranding();
    const [showPlayButton, setShowPlayButton] = useState(!interval?.automatic);
    const carouselContext = useContext(CarouselContext);
    const [carouselState, setCarouselState] = useState(carouselContext || null);
    const sliderRef = React.useRef<HTMLDivElement>(null);

    const getCurrentSlideIndex = () => {
        return carouselState?.state?.currentSlide;
    };

    const ariaLabelName = (labelName: string, index: number) => {
        return labelName?.replace("0", index.toString());
    };

    useEffect(() => {
        const onChange = (): void => {
            setCarouselState({ ...carouselContext });

            if (sliderRef.current) {
                // Find all slides within this specific carousel
                const slides = sliderRef.current.getElementsByClassName("carousel__slide");
                Array.from(slides).forEach((slide, index) => {
                    const isCurrentSlide = index === carouselContext.state.currentSlide;
                    // Find all focusable elements within the slide
                    const focusableElements = slide.querySelectorAll(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
                    );

                    focusableElements.forEach((element) => {
                        if (isCurrentSlide) {
                            element.setAttribute("tabindex", "0");
                        } else {
                            element.setAttribute("tabindex", "-1");
                        }
                    });
                });
            }
        };

        carouselContext.subscribe(onChange);
        return () => carouselContext.unsubscribe(onChange);
    }, [carouselContext]);

    useEffect(() => {
        const dotGroupElements = document?.querySelectorAll("[data-dotcontrols='carousel-dot-controls']");
        dotGroupElements?.forEach((groupElements) => {
            const buttonElement = groupElements?.querySelectorAll("button");
            buttonElement?.forEach((element, index) => {
                const ariaLabel = ariaLabelName(carouselSlideDotAriaLabel, index + 1);
                element?.setAttribute("aria-label", ariaLabel);
                element?.setAttribute("title", ariaLabel);
            });
        });
    }, []);

    return (
        <div ref={sliderRef}>
            <Slider
                className={!fullWidth && roundedCorners && "carousel__border-radius"}
                onMouseOver={() => {
                    pauseOnHover && setPlaying(false);
                }}
                onMouseLeave={() => {
                    pauseOnHover && !showPlayButton && setPlaying(interval?.automatic);
                }}
                onTouchStart={() => {
                    setPlaying(false);
                }}
                onTouchEnd={() => {
                    setPlaying(interval?.automatic);
                }}
                data-test-id="ImageCarousel.Slides"
                role="group"
                aria-roledescription="carousel"
                aria-live="off"
                aria-label={carouselSliderAriaLabel}
            >
                {React.Children?.map(children, (child, index: number) => {
                    return (
                        <Slide
                            index={index}
                            key={index}
                            data-test-id={`ImageCarousel.Slide.${index}`}
                            role="group"
                            aria-roledescription="slide"
                            aria-label={ariaLabelName(carouselSlideAriaLabel, index + 1)}
                        >
                            {child}
                        </Slide>
                    );
                })}
            </Slider>
            <div
                className={classNames(style.carousel__controls, headerVariant && style.carousel__headerVariant)}
                data-test-id="ImageCarousel.Controls"
            >
                {jnjFullBranded ? (
                    <div className={style.carousel__controls__progress}>
                        <div
                            className={style.carousel__controls__progress_indicator}
                            style={{
                                width: `${100 / carouselContext?.state?.totalSlides}%`,
                                insetInlineStart: `${
                                    (getCurrentSlideIndex() * 100) / carouselContext?.state?.totalSlides
                                }%`,
                            }}
                        ></div>
                    </div>
                ) : null}
                <div
                    className={classNames(
                        style.carousel__controls_inner,
                        roundedCorners && style.carousel__controls_inner_withRoundedCorners,
                    )}
                >
                    {jnjFullBranded ? null : (
                        <DotGroup
                            data-dotcontrols={"carousel-dot-controls"}
                            className={classNames(style.carousel__controls__dotgroup)}
                            data-test-id="ImageCarousel.Controls.Dots"
                            onClick={(): void => {
                                setShowPlayButton(true);
                                actionButtonClick && actionButtonClick("carousel-indicator", getCurrentSlideIndex());
                            }}
                        />
                    )}
                    <ButtonBack
                        className={classNames(style.carousel__controls__button, style.carousel__controls__button_back)}
                        data-test-id="ImageCarousel.Controls.Back"
                        onClick={() => {
                            setShowPlayButton(true);
                            actionButtonClick && actionButtonClick("left-arrow", getCurrentSlideIndex());
                        }}
                        aria-label={carouselPreviousButtonAriaLabel}
                        title={carouselPreviousButtonAriaLabel}
                    >
                        <Icon
                            className={classNames(style.carousel__controls__button__icon)}
                            icon={jnjFullBranded ? jnjArrowLeft : mdiChevronLeft}
                            type={jnjFullBranded ? "jnj" : "mdi"}
                            size={jnjFullBranded ? "small" : "medium"}
                            color={jnjFullBranded ? "white" : "primary"}
                        />
                    </ButtonBack>
                    <ButtonNext
                        className={classNames(style.carousel__controls__button, style.carousel__controls__button_next)}
                        data-test-id="ImageCarousel.Controls.Next"
                        onClick={() => {
                            setShowPlayButton(true);
                            actionButtonClick && actionButtonClick("right-arrow", getCurrentSlideIndex());
                        }}
                        aria-label={carouselNextButtonAriaLabel}
                        title={carouselNextButtonAriaLabel}
                    >
                        <Icon
                            className={classNames(style.carousel__controls__button__icon)}
                            icon={jnjFullBranded ? jnjArrowRight : mdiChevronRight}
                            type={jnjFullBranded ? "jnj" : "mdi"}
                            size={jnjFullBranded ? "small" : "medium"}
                            color={jnjFullBranded ? "white" : "primary"}
                        />
                    </ButtonNext>
                    {!jnjFullBranded || interval?.automatic ? (
                        <ButtonPlay
                            className={classNames(
                                style.carousel__controls__button,
                                style.carousel__controls__button_play_pause,
                            )}
                            data-test-id={`ImageCarousel.Controls.${showPlayButton ? "Play" : "Pause"}`}
                            onClick={(): void => setShowPlayButton((showPlayButton) => !showPlayButton)}
                            aria-label={showPlayButton ? carouselPlayButtonAriaLabel : carouselPauseButtonAriaLabel}
                            title={showPlayButton ? carouselPlayButtonAriaLabel : carouselPauseButtonAriaLabel}
                        >
                            <Icon
                                className={style.carousel__controls__button__icon}
                                icon={
                                    showPlayButton
                                        ? jnjFullBranded
                                            ? jnjVideoPlay
                                            : mdiPlay
                                        : jnjFullBranded
                                          ? jnjPause
                                          : mdiPause
                                }
                                type={jnjFullBranded ? "jnj" : "mdi"}
                                size={jnjFullBranded ? "small" : "medium"}
                                color={jnjFullBranded ? "white" : "primary"}
                            />
                        </ButtonPlay>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default CarouselContents;
